/**
 * B-19 — planned-vs-actual distance pairing (the single derivation helper).
 *
 * The rule (spec §2): ONE helper, every surface reads through it. WeekPlan
 * (mobile + desktop) and PlanTable both pair the day's PLANNED objective with
 * the stored ACTUAL via this pure function, so a second mile-parsing / actual-
 * reading idiom never scatters across the surfaces and the "X of Y" copy string
 * is authored in exactly one place.
 *
 * Sources (spec §1.1 / RATIFIABLE 1):
 *   - actualValue / actualUnit = `plan_days.actual_distance` /
 *     `actual_distance_unit` ONLY — the already-authoritative accumulator the
 *     "add to today's session" path reconciles GPS + HealthKit + manual into.
 *     NO `runs.distance_m` time-mapped fallback in v1 (a second mapping path
 *     re-opens the attribution surface B-17b just closed).
 *   - plannedText / plannedMiles = the SURFACE'S existing parsed planned figure
 *     (WeekPlan: `deriveDayMetric`; PlanTable: `extractMileage`). The planned
 *     side is parsed text, not a canonical number (spec §1.3), so the pairing is
 *     display-only — never a numeric planned-vs-actual delta (§4 clinical fence).
 *
 * Clinical fence (spec §4, Y2 no-gamification, u16 lens): the returned `state`
 * (`over`/`match`/`under`) drives PRESENTATION only. It is NOT a score and is
 * NEVER a numeric delta/percentage. `over` and `under` carry the SAME weight as
 * `match`; the `copy` string is neutral factual ("5.0 of 4 mi") — no "+1.0", no
 * "%", no "bonus"/"streak"/"crushed"-style framing. Over-running a plan is a
 * load-management risk for a MINOR; this helper must never render it as a win.
 *
 * Pure: no I/O. The caller passes the already-loaded plan-day fields.
 */

export type ActualState = 'no_actual' | 'match' | 'over' | 'under';
export type DistanceUnit = 'mi' | 'km';

/* ===========================================================================
 * B55 — imported-workout plan-day credit.
 *
 * FOUNDER REPORT (2026-08-29): a workout recorded by his ring, imported through
 * the in-app "sync with Apple workouts" flow, showed up correctly in Runs but
 * did NOT credit that day's planned session. He marked the day complete by hand.
 *
 * ROOT CAUSE (not in this file): the two capture paths are asymmetric.
 *   - IN-APP run  → POST /api/runs, and then the post-run sheet OFFERS "add to
 *     today's session"; a yes PUTs `plan_days` with
 *     `completionStatus:'completed' + actualDistance` (run-spike.tsx `addToToday`).
 *   - IMPORTED workout → `lib/health/workoutImport.ts` POSTs /api/runs and
 *     STOPS. Nothing ever writes `plan_days`, so the objective stays unfilled.
 *
 * THE RULE (one sentence, founder-ratifiable):
 *   A workout imported from Apple Health credits the plan day it STARTED on —
 *   same local calendar day, any distance — filling that day's objective and
 *   counting toward the week, unless the day already carries a logged actual or
 *   an explicit skip (those always win), and several imported workouts on one
 *   day sum.
 *
 * Deliberately SIMPLE (§ the brief): no pace matching, no workout-type
 * similarity, no distance-tolerance gate on whether to credit. Fancier matching
 * is future work — see the notes on each rule below.
 *
 * WHY THIS LAYER AND NOT THE IMPORTER: the durable fix is for the import to
 * write `plan_days` the way `addToToday` does. That lives in `lib/health`,
 * owned by another lane tonight, so this is the read-model credit: it makes the
 * athlete's own week read correctly, and it SELF-RETIRES the moment the durable
 * write ships — a stored actual always wins (rule 3), so a durably-credited day
 * never routes through here at all.
 *
 * This does NOT re-open the B-19 §1.1 fence ("no runs.distance_m fallback").
 * That fence protects `plan_days.actual_distance` as the ONE accumulator; this
 * credit never competes with it — it only fills a day the accumulator was never
 * given a chance to record.
 * ======================================================================== */

/** Meters per statute mile. Mirrors `lib/run/geo`'s METERS_PER_MILE; duplicated
 *  as a literal so this helper stays import-free and pure. */
const METERS_PER_MILE = 1609.344;

/**
 * `runs.source` written by the HealthKit importer (`workoutImport.ts`'s
 * `HK_IMPORT_SOURCE`). In-app GPS runs write `shell-<engine>`. Matched by
 * PREFIX so a future `healthkit-*` variant still credits without a change here.
 */
export const IMPORTED_RUN_SOURCE_PREFIX = 'healthkit';

/** One row from `GET /api/runs`, narrowed to the fields the credit reads. */
export interface PlanCreditRun {
  id: string;
  /** ISO instant the workout started. */
  started_at: string;
  /** `runs.distance_m` (numeric — may arrive as a string). */
  distance_m: number | string | null | undefined;
  /** `runs.source`. Absent on legacy payloads → treated as NOT imported. */
  source?: string | null;
  /** B257 — the athlete's own opt-out: set from the run itself when an
   *  imported workout should not count toward its plan day. Non-null → this
   *  run contributes nothing here, undo-able from the same control. Absent /
   *  null → byte-identical to pre-B257. */
  plan_credit_excluded_at?: string | null;
}

/**
 * RULE 4 — IMPORTED SOURCE ONLY. An in-app run is deliberately NOT auto-
 * credited here: its post-run sheet already ASKS ("add to today's session"),
 * and an athlete who answered no must not have that answer overridden by a
 * silent derivation. The import path has no such consent moment, which is
 * exactly why it needs this.
 */
export function isImportedRun(source: string | null | undefined): boolean {
  return typeof source === 'string' && source.trim().toLowerCase().startsWith(IMPORTED_RUN_SOURCE_PREFIX);
}

/** Same local calendar day (Y/M/D). Local — never UTC: the athlete's own day
 *  boundary is the one the Runs surface and the today-pin already use. */
function isSameLocalDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export interface PlanDayCreditInput {
  /** Local-midnight Date of the plan day's calendar cell. */
  dayDate: Date;
  /** `plan_days.actual_distance`. Non-null — INCLUDING the authoritative 0 that
   *  encodes a skip — means the day is already logged. */
  storedActual: number | null | undefined;
  /** `plan_days.completion_status`. */
  completionStatus?: 'completed' | 'skipped' | 'upcoming' | null;
  /** The athlete's recent runs (imported AND in-app; this filters). */
  runs?: PlanCreditRun[] | null;
}

export interface PlanDayCredit {
  /** Miles credited from imported workouts, rounded to 0.01 (the same
   *  precision `addToToday` writes). Null when nothing credited — the caller
   *  then behaves byte-identically to pre-B55. */
  miles: number | null;
  /** How many distinct imported workouts credited the day. 0 when none. */
  workouts: number;
}

const NO_CREDIT: PlanDayCredit = { miles: null, workouts: 0 };

/**
 * Resolve what an imported workout credits to ONE plan day.
 *
 * The rules, in the order they apply:
 *
 *  1. SAME LOCAL DAY, BY START. A workout credits the plan day whose calendar
 *     date equals its `started_at` in the athlete's local timezone. A run that
 *     SPANS MIDNIGHT credits the day it started — one workout, one day, never
 *     split across two. (Future work: an end-of-run tiebreak for the rare
 *     23:50 start; not worth the ambiguity today.)
 *  2. ANY DISTANCE CREDITS. No pace, type, or distance-similarity test — a
 *     0.8 mi shakeout credits a planned 4 mi day, and the day then reads
 *     "0.8 of 4 mi" honestly rather than silently unfilled. (Future work:
 *     type/pace-aware matching once there is a reason to distrust the match.)
 *     A ZERO-distance workout does NOT credit: 0 is reserved for the
 *     authoritative skip (B-19 §5), so crediting it would be indistinguishable.
 *  3. A LOGGED ACTUAL ALWAYS WINS — and this is also the DEDUPE RULE. If
 *     `plan_days.actual_distance` is non-null (a value the athlete or the
 *     in-app "add to today's session" flow already wrote, or the 0 of a skip),
 *     imports never add to it and never replace it. So an imported duplicate of
 *     an in-app run that already credited the day CANNOT double-count. (The
 *     importer's own >50% overlap filter is the first guard; this is the
 *     second, and it holds even when the import landed first.)
 *  4. IMPORTED SOURCE ONLY — see `isImportedRun`.
 *  5. AN EXPLICIT SKIP IS NEVER OVERRIDDEN. `completion_status:'skipped'` is
 *     the athlete saying the session did not happen; a stray imported workout
 *     does not get to argue.
 *  6. SEVERAL WORKOUTS ON ONE DAY SUM — a doubles day is one day's load. This
 *     matches `addToToday`'s grammar, which adds the run to the day's existing
 *     actual rather than replacing it.
 *  7. DUPLICATE IMPORTED ROWS COLLAPSE. Deduped by run `id`, then by an
 *     identical `(started_at, distance)` signature — the shape a re-import
 *     would take if the `client_key` partial-unique were ever bypassed.
 *  8. AN EXPLICIT PER-RUN EXCLUSION IS HONORED (B257). The athlete can mark
 *     one imported run, from the run itself, as not counting toward its plan
 *     day — undo-able the same way. A marked run (`plan_credit_excluded_at`
 *     non-null) contributes nothing here, exactly as if it had never been
 *     imported. This is the RUN's own opt-out, orthogonal to rules 3 and 5
 *     (the PLANNED DAY's word — a stored actual, an explicit skip).
 *
 * (Rest days never reach here — the caller computes the credit only for a
 * planned, non-rest session.)
 *
 * Pure: no I/O, no clock read. The caller passes the day's date and rows.
 */
export function creditForPlanDay(input: PlanDayCreditInput): PlanDayCredit {
  const { dayDate, storedActual, completionStatus, runs } = input;

  // Rule 3 — a logged actual (incl. the skip's authoritative 0) wins outright.
  if (storedActual !== null && storedActual !== undefined && Number.isFinite(storedActual)) {
    return NO_CREDIT;
  }
  // Rule 5 — an explicit skip is the athlete's word on the day.
  if (completionStatus === 'skipped') return NO_CREDIT;
  if (!Array.isArray(runs) || runs.length === 0) return NO_CREDIT;
  if (!(dayDate instanceof Date) || Number.isNaN(dayDate.getTime())) return NO_CREDIT;

  const seenIds = new Set<string>();
  const seenSignatures = new Set<string>();
  let meters = 0;
  let workouts = 0;

  for (const run of runs) {
    if (!run || !isImportedRun(run.source)) continue; // rule 4
    if (run.plan_credit_excluded_at) continue; // rule 8 — B257 opt-out
    const startedMs = Date.parse(run.started_at);
    if (!Number.isFinite(startedMs)) continue;
    if (!isSameLocalDay(new Date(startedMs), dayDate)) continue; // rule 1
    const distance = Number(run.distance_m);
    if (!Number.isFinite(distance) || distance <= 0) continue; // rule 2

    // Rule 7 — collapse duplicates by id, then by (start, distance) signature.
    const id = typeof run.id === 'string' ? run.id : '';
    if (id && seenIds.has(id)) continue;
    const signature = `${startedMs}|${Math.round(distance * 10)}`;
    if (seenSignatures.has(signature)) continue;
    if (id) seenIds.add(id);
    seenSignatures.add(signature);

    meters += distance; // rule 6
    workouts += 1;
  }

  if (workouts === 0) return NO_CREDIT;
  return { miles: Math.round((meters / METERS_PER_MILE) * 100) / 100, workouts };
}

/**
 * RATIFIABLE 2 — tolerance band for `match` vs `over`/`under` = max(3%, 0.1 mi).
 * Config-tunable constants (NOT schema). A 4.02-vs-4.0 rounding blip stays
 * `match`. Loosened from the Rung-2 `max(2%, 80 m)` idiom because the planned
 * side here is parsed text, not canonical meters.
 */
export const B19_TOLERANCE_PCT = 0.03;
export const B19_TOLERANCE_FLOOR_MI = 0.1;

/** NIST survey-mile ↔ km factor (matches useWeeklyPlan's MILES_PER_KM). */
const MI_PER_KM = 0.621371;

/** Convert a value in `unit` to miles (the internal comparison unit). */
export function toMiles(value: number, unit: DistanceUnit): number {
  return unit === 'km' ? value * MI_PER_KM : value;
}

/** Convert miles into `unit` (for rendering in the athlete's display unit). */
export function fromMiles(miles: number, unit: DistanceUnit): number {
  return unit === 'km' ? miles / MI_PER_KM : miles;
}

export interface ActualForPlanDayInput {
  /** The surface's existing parsed planned display figure, e.g. "4 mi". Null
   *  when the planned side can't be parsed (mixed-pace interval day, F-112e). */
  plannedText: string | null;
  /** The numeric planned distance in miles for the tolerance comparison. Null /
   *  <= 0 → no objective to compare against (rest / unparseable). */
  plannedMiles: number | null;
  /** `plan_days.actual_distance`. */
  actualValue: number | null | undefined;
  /** `plan_days.actual_distance_unit`. */
  actualUnit: DistanceUnit | null | undefined;
  /** Athlete display unit (`athlete_context.distance_unit`). Default 'mi'. */
  displayUnit?: DistanceUnit;
}

export interface ActualForPlanDayResult {
  /** Echoed unchanged — the surface's parsed planned figure. */
  plannedText: string | null;
  /** The actual, converted to the athlete's display unit, rounded to 0.1.
   *  Null when there is no stored actual. */
  actualValue: number | null;
  /** The display unit the `actualValue` / `copy` are expressed in. Null when
   *  there is no stored actual. */
  actualUnit: DistanceUnit | null;
  /** Coarse three-way display state (NEVER a score, §4). `no_actual` when the
   *  day carries no stored actual — the surface renders planned-only, unchanged. */
  state: ActualState;
  /** The shared neutral-factual "X of Y" copy string. Null when `no_actual`. */
  copy: string | null;
}

/** One-decimal distance format ("5.0", "3.7") — matches the spec examples. */
function fmt(n: number): string {
  return n.toFixed(1);
}

/** The unit a planned display string is expressed in ("6 km" → km, else mi).
 *  The real callers (deriveDayMetric / extractMileage) emit miles, except that
 *  useWeeklyPlan passes a km athlete's own km figure when the plan states km
 *  (B396), which `plannedLabel` then keeps verbatim. */
function unitOfPlannedText(text: string): DistanceUnit {
  return /\bk(?:m|ilomet)/i.test(text) ? 'km' : 'mi';
}

/**
 * ⚖️ B28 — the planned figure is a DURATION, not a distance ("45 min", "20 to 30 min").
 *
 * `deriveDayMetric` emits minutes whenever the engine authored a day in TIME
 * rather than distance, which it does for cross-training and tempo always, and
 * for every easy/long run belonging to an athlete whose pace could not be
 * resolved. Pairing that with a distance actual produced mixed-unit nonsense:
 * "3.6 of 45 min" — a distance number wearing a time label, which reads as a
 * catastrophic shortfall. There is no planned DISTANCE on such a day, so the
 * honest render is the graceful form this file already has ("3.6 mi logged"),
 * never an invented comparison.
 *
 * (`classifyActual` was already safe here: plannedMiles is 0 on a minutes day,
 * which it treats as "no objective" → 'match'. Only the COPY was lying.)
 */
function isDurationOnly(text: string): boolean {
  const hasMinutes = /\bmin(?:ute)?s?\b/i.test(text);
  const hasDistance = /\d\s*(?:miles?|mi|kilometers?|kilometres?|km)\b/i.test(text);
  return hasMinutes && !hasDistance;
}

/**
 * Render the planned figure in the athlete's DISPLAY unit so BOTH halves of the
 * "X of Y" copy share one unit (a km athlete must never see "8.0 of 5 mi" — km
 * number, mi label, which reads as a huge overshoot even when classify says
 * match). When the planned text is already in the display unit it's kept
 * verbatim (preserves the natural integer form "4 mi", not "4.0 mi"); when it
 * differs, the canonical `plannedMiles` is converted + relabeled.
 */
function plannedLabel(
  plannedText: string | null,
  plannedMiles: number | null,
  displayUnit: DistanceUnit,
): string | null {
  // A day that states no distance reads '?' (`deriveDayMetric`'s no-figure mark, with 0 miles): it reads like a
  // minutes-only day, never "0.0 km" (harvest 2026-10-20 M1).
  if (!plannedText || plannedText === '?') return null;
  // B28 — a duration-only planned figure has no distance to compare against.
  if (isDurationOnly(plannedText)) return null;
  if (unitOfPlannedText(plannedText) === displayUnit) return plannedText;
  if (plannedMiles == null) return plannedText; // can't convert — best effort
  const converted = Math.round(fromMiles(plannedMiles, displayUnit) * 10) / 10;
  return `${fmt(converted)} ${displayUnit}`;
}

/**
 * Coarse over/match/under classification against the tolerance band. Exported
 * so the boundary behavior (incl. the 0.1 mi floor) is unit-pinnable directly.
 *
 * - actual missing → 'no_actual'.
 * - planned missing / <= 0 → 'match' (no objective to compare; neutral — all
 *   three states render with equal weight anyway, §4, and the copy reads
 *   "{actual} {unit} logged", B425).
 * - else within max(3% of planned, 0.1 mi) → 'match'; above → 'over'; below →
 *   'under'. The band is INCLUSIVE ("within" → match).
 */
export function classifyActual(
  plannedMiles: number | null,
  actualMiles: number | null,
): ActualState {
  if (actualMiles == null) return 'no_actual';
  if (plannedMiles == null || plannedMiles <= 0) return 'match';
  const tolerance = Math.max(plannedMiles * B19_TOLERANCE_PCT, B19_TOLERANCE_FLOOR_MI);
  const diff = actualMiles - plannedMiles;
  if (Math.abs(diff) <= tolerance) return 'match';
  return diff > 0 ? 'over' : 'under';
}

/**
 * Derive the display-ready planned-vs-actual DTO for one plan day. See the file
 * header for the source + clinical rules. Returns `state: 'no_actual'` (and a
 * null `copy`) whenever there is no valid stored actual — the caller then
 * renders exactly as today (planned only), so a day with no actual is byte-
 * identical to current behavior.
 */
export function actualForPlanDay(input: ActualForPlanDayInput): ActualForPlanDayResult {
  const plannedText = input.plannedText ?? null;
  const plannedMiles = input.plannedMiles ?? null;
  const displayUnit: DistanceUnit = input.displayUnit === 'km' ? 'km' : 'mi';
  const { actualValue, actualUnit } = input;

  // A valid stored actual is a PAIRED value+unit (the integrity `actualDistance.js`
  // enforces: both-present or both-null). Anything else → treat as no actual.
  //
  // Skip-day resolution (spec §1.1 stores a skip as actual_distance=0, but §5
  // says a skipped day → NO pairing): actual === 0 → NO day-row pairing
  // (planned-only render). The authoritative 0 still counts toward the reward
  // strip's "done" — that lives in useWeeklyPlan, which reads the raw 0 directly
  // and does NOT route the strip through this suppression. Founder-ack: contradiction
  // between §1.1 and §5 resolved toward §5 (no row pairing on a skip).
  const hasActual =
    typeof actualValue === 'number' &&
    Number.isFinite(actualValue) &&
    actualValue !== 0 &&
    (actualUnit === 'mi' || actualUnit === 'km');

  if (!hasActual) {
    return {
      plannedText,
      actualValue: null,
      actualUnit: null,
      state: 'no_actual',
      copy: null,
    };
  }

  const actualMiles = toMiles(actualValue as number, actualUnit as DistanceUnit);
  // Convert to the athlete's display unit before comparing/rendering — a mi↔km
  // difference between planned text and stored actual is a presentation
  // conversion, never an over/under (spec §2).
  const actualDisplay = Math.round(fromMiles(actualMiles, displayUnit) * 10) / 10;
  const state = classifyActual(plannedMiles, actualMiles);

  const actualStr = fmt(actualDisplay);
  // Shared "X of Y" grammar, BOTH halves in the display unit. When the planned
  // figure is known it carries the unit ("5.0 of 4 mi"; km athlete → "8.0 of
  // 8.0 km"); when there is no planned figure (F-112e, a minutes-only day) the
  // line states what was logged and keeps the unit on it ("5.0 mi logged", B425,
  // founder 2026-10-21), no "of" and no placeholder. Never emit a wrong or
  // mixed-unit planned figure.
  const pLabel = plannedLabel(plannedText, plannedMiles, displayUnit);
  const copy = pLabel ? `${actualStr} of ${pLabel}` : `${actualStr} ${displayUnit} logged`;

  return {
    plannedText,
    actualValue: actualDisplay,
    actualUnit: displayUnit,
    state,
    copy,
  };
}

/**
 * B-19 Today-hero completed-state sub-line (frame §1-§8). The HERO speaks a
 * DIFFERENT grammar from the list surfaces: "You ran {actual} {unit}[ · planned
 * {N}]" (frame §8 one-grammar set MATCH#1/OVER#1/UNDER#1) — NOT the list's "X of
 * Y mi". This deriver is a pure CONSUMER of `actualForPlanDay`: it reuses that
 * helper's classification + display-unit-converted figures verbatim and never
 * re-derives the tolerance band or the unit conversion (§2 clinical fence — the
 * hero must not open a second mile-parsing / over-under idiom).
 *
 * Returns `null` whenever the sub-line must NOT mount (frame §3): no stored
 * actual (no_actual / capture-off / rest / cross-train / skipped, all folded
 * into `state:'no_actual'` by the helper). The caller then renders the hero
 * byte-identically to today (headline only).
 *
 * Grammar (frame §2 / §8):
 *   - match → head only, no planned tail (they hit the plan; restating it is
 *     noise). `You ran 4.0 mi`.
 *   - over / under → head + a quiet `· planned {N}` tail. `You ran 5.0 mi ·
 *     planned 4` / `You ran 3.0 mi · planned 4`. IDENTICAL structure across the
 *     two — only the number differs (no valence: no +delta, no %, no color).
 *   - mixed-pace (F-112e, plannedText null → plannedLabel null) → the tail
 *     is dropped (frame §3, B425): the head stands alone, never a wrong parsed
 *     planned figure and no placeholder.
 *
 * The planned tail number is the SAME display-unit figure the list surface pairs
 * (`plannedLabel`), with the redundant unit suffix stripped (the unit already
 * rides the actual head — a km athlete sees both figures in km, §4 / task copy
 * discipline). `head`/`tail`/`text` are split so the caller can render the tail
 * one neutral step quieter (frame §5) WITHOUT the split ever encoding valence.
 */
export interface HeroActualSubline {
  /** Full sub-line text — the copy-pin + accessible-read target. */
  text: string;
  /** "You ran 5.0 mi" — the actual, always the SAME weight/color for all three
   *  states (§2). */
  head: string;
  /** "planned 4" — the quiet planned tail; null for `match` and for a day with
   *  no planned figure (no tail). Bare number, no unit (the unit is on `head`). */
  tail: string | null;
  /** Echoed classification. NEVER styles the line (§2) — exposed for tests /
   *  callers that assert equal weight regardless of state. `no_actual` never
   *  reaches here (the deriver returns null first). */
  state: 'match' | 'over' | 'under';
}

/** Strip the trailing unit token from a planned label ("4 mi" → "4", "8.0 km" →
 *  "8.0") so the hero tail carries only the number (§8 "planned 4"). */
function stripPlannedUnit(label: string): string {
  return label.replace(/\s*(?:mi|km)\s*$/i, '').trim();
}

export function heroActualSubline(input: ActualForPlanDayInput): HeroActualSubline | null {
  const res = actualForPlanDay(input);
  // Frame §3 — no stored actual → the sub-line does not mount (byte-identical
  // hero). The helper folds capture-off / rest / cross-train / skipped / skip-
  // zero into `no_actual`, so this single guard covers every no-mount case.
  if (res.state === 'no_actual' || res.actualValue == null || res.actualUnit == null) {
    return null;
  }

  const head = `You ran ${fmt(res.actualValue)} ${res.actualUnit}`;

  if (res.state === 'match') {
    // §2 — matched the plan → no `planned N` tail (redundant).
    return { text: head, head, tail: null, state: 'match' };
  }

  // over / under — the quiet planned tail. Reuse the list surface's planned
  // figure (display-unit, §4 convert-before-compare), unit stripped. With no
  // planned figure (mixed-pace: plannedText null → plannedLabel null) there is
  // no tail at all (B425, founder 2026-10-21): the head stands alone.
  const displayUnit: DistanceUnit = input.displayUnit === 'km' ? 'km' : 'mi';
  const pLabel = plannedLabel(res.plannedText, input.plannedMiles ?? null, displayUnit);
  if (!pLabel) return { text: head, head, tail: null, state: res.state };
  const tail = `planned ${stripPlannedUnit(pLabel)}`;
  return { text: `${head} · ${tail}`, head, tail, state: res.state };
}

/**
 * W1-TODAY-HERO (2.0 Today, `variant="v2"`) — string 18 of the ratified set,
 * "You ran 7.2 km · planned 7 km": the same sub-line `heroActualSubline`
 * derives, with the planned figure carrying its unit (the legacy tail is the
 * bare number, "planned 4"). A pure read of the sub-line a Day already carries
 * (`Day.actual.hero`), so the gate that decides whether a sub-line exists at all
 * (b19_actuals_v1 + a stored actual) is exactly the existing one.
 *
 * `plannedDisplay` is the day's own planned figure as the week cell prints it
 * ("7 km", "4 mi": `Day.mileageInUnit ?? Day.mileage`). When it is a single
 * distance in the same unit as the actual it is the planned figure, so the card
 * never says "7 km" in the stat line and "planned 6.9 km" under it (the legacy
 * tail is the planned miles converted back to km and rounded twice). Absent or
 * not a match, the legacy tail's number gets the actual's unit.
 *
 * Grammar notes: a match keeps no tail (as the legacy sub-line); a day with no
 * planned figure has no tail on either surface (B425), because a planned
 * figure that cannot be stated is better left unsaid.
 */
export function heroSublineWithUnit(
  hero: HeroActualSubline | null | undefined,
  plannedDisplay?: string | null,
): { text: string; head: string; tail: string | null } | null {
  if (!hero) return null;
  const unit = /\s(mi|km)$/i.exec(hero.head)?.[1].toLowerCase();
  const legacy = hero.tail ? /^planned\s+(\d+(?:\.\d+)?)$/.exec(hero.tail)?.[1] : undefined;
  if (!legacy || !unit) return { text: hero.head, head: hero.head, tail: null };
  const own = /^(\d+(?:\.\d+)?)\s*(mi|km)$/i.exec((plannedDisplay ?? '').trim());
  const planned = own && own[2].toLowerCase() === unit ? own[1] : legacy;
  const tail = `planned ${planned} ${unit}`;
  return { text: `${hero.head} · ${tail}`, head: hero.head, tail };
}
